import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUpload, 
  FaTrash, 
  FaDownload, 
  FaFilePdf,
  FaFile,
  FaCalendarAlt,
  FaPlus
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminResumeManager = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resumeData, setResumeData] = useState({
    template: 'modern',
    include_experience: true,
    include_projects: true,
    include_education: true,
    include_skills: true,
    focus: 'fullstack'
  });

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const response = await apiService.getResumeVersions();
      setResumes(response.data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to fetch resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('resume', selectedFile);
      formData.append('version', `v${resumes.length + 1}`);

      await apiService.uploadResume(formData);
      toast.success('Resume uploaded successfully');
      setSelectedFile(null);
      fetchResumes();
    } catch (error) {
      toast.error('Failed to upload resume');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resume version?')) {
      try {
        await apiService.deleteResume(id);
        toast.success('Resume deleted successfully');
        fetchResumes();
      } catch (error) {
        toast.error('Failed to delete resume');
      }
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await apiService.generateResume(resumeData);
      toast.success('Resume generated successfully');
      // Download the generated resume
      window.open(response.data.download_url, '_blank');
      fetchResumes();
    } catch (error) {
      toast.error('Failed to generate resume');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Resume Manager</h1>
          <p className="text-gray-400 mt-1">Manage your resume versions</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="glass rounded-2xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Upload New Resume</h2>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1">
            <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-all duration-300">
              <div className="text-center">
                <FaUpload className="text-2xl text-gray-400 mx-auto mb-2" />
                <span className="text-gray-400 text-sm">
                  {selectedFile ? selectedFile.name : 'Drop PDF here or click to browse'}
                </span>
                <span className="block text-xs text-gray-500 mt-1">PDF only, Max 5MB</span>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={handleFileSelect}
              />
            </label>
          </div>
          {selectedFile && (
            <div className="flex items-center gap-4">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => setSelectedFile(null)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Generate Resume Section */}
      <div className="glass rounded-2xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FaPlus className="text-primary-400" />
          Generate Custom Resume
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Template
            </label>
            <select
              value={resumeData.template}
              onChange={(e) => setResumeData({ ...resumeData, template: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="modern">Modern</option>
              <option value="classic">Classic</option>
              <option value="minimal">Minimal</option>
              <option value="creative">Creative</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Focus
            </label>
            <select
              value={resumeData.focus}
              onChange={(e) => setResumeData({ ...resumeData, focus: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="fullstack">Full Stack Development</option>
              <option value="frontend">Frontend Development</option>
              <option value="backend">Backend Development</option>
              <option value="blockchain">Blockchain Development</option>
              <option value="embedded">Embedded Systems</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <label className="flex items-center gap-2 text-gray-300 text-sm">
            <input
              type="checkbox"
              checked={resumeData.include_experience}
              onChange={(e) => setResumeData({ ...resumeData, include_experience: e.target.checked })}
              className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
            />
            Experience
          </label>
          <label className="flex items-center gap-2 text-gray-300 text-sm">
            <input
              type="checkbox"
              checked={resumeData.include_projects}
              onChange={(e) => setResumeData({ ...resumeData, include_projects: e.target.checked })}
              className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
            />
            Projects
          </label>
          <label className="flex items-center gap-2 text-gray-300 text-sm">
            <input
              type="checkbox"
              checked={resumeData.include_education}
              onChange={(e) => setResumeData({ ...resumeData, include_education: e.target.checked })}
              className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
            />
            Education
          </label>
          <label className="flex items-center gap-2 text-gray-300 text-sm">
            <input
              type="checkbox"
              checked={resumeData.include_skills}
              onChange={(e) => setResumeData({ ...resumeData, include_skills: e.target.checked })}
              className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
            />
            Skills
          </label>
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          className="mt-4 px-6 py-2 bg-gradient-to-r from-accent-500 to-accent-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-accent-500/30 transition-all duration-300 disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate Resume'}
        </button>
      </div>

      {/* Resume List */}
      <h2 className="text-lg font-semibold text-white mb-4">Resume Versions</h2>
      <div className="space-y-4">
        {resumes.map((resume) => (
          <motion.div
            key={resume.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-4 hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400">
                  <FaFilePdf size={24} />
                </div>
                <div>
                  <h4 className="text-white font-medium">{resume.filename}</h4>
                  <div className="flex items-center gap-3 text-sm text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-xs" />
                      {new Date(resume.created_at).toLocaleDateString()}
                    </span>
                    <span>{(resume.file_size / 1024).toFixed(1)} KB</span>
                    <span className="text-xs text-primary-400">v{resume.version}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={resume.download_url}
                  download
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-primary-400"
                >
                  <FaDownload size={14} />
                </a>
                <button
                  onClick={() => handleDelete(resume.id)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 transition-all duration-300 text-gray-400 hover:text-red-400"
                >
                  <FaTrash size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {resumes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No resumes uploaded. Upload your first resume!</p>
        </div>
      )}
    </div>
  );
};

export default AdminResumeManager;